import React, {
  Dispatch, ReactElement, SetStateAction, useState,
} from 'react';
import {
  View, Text, StyleSheet, FlatList,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../../constants/Colors';
import InfoIcon16 from '../../../components/16pxIcons/InfoIcon16';
import PressableStyled from '../../../components/PressableStyled';
import SelectionModal from '../../../components/SelectionModal';
import Style from '../../../constants/Style';
import { MetricProps } from '../../../graphs/AirQualityGraph';
import { safetyLevelTextColor } from './AirQuality/components/MetricValueCard';

interface OverviewMobileProps {
  metrics: MetricProps[],
  liveValues: number[],
  setSelectedMetric: Dispatch<SetStateAction<MetricProps>>,
  parametersInfoContent?: ReactElement,
}

const OverviewMobile = ({
  metrics, liveValues, setSelectedMetric, parametersInfoContent,
}: OverviewMobileProps) => {
  const [descriptionModalVisible, setDescriptionModalVisible] = useState<boolean>(false);

  return <View style={styles.container}>
    {!!parametersInfoContent && <PressableStyled
      style={styles.header}
      onPress={() => setDescriptionModalVisible(true)}
    >
      <Text style={styles.infoLabel}>{'Parameters information'}</Text>
      <InfoIcon16 />
    </PressableStyled>}
    <FlatList
      style={styles.list}
      data={metrics}
      renderItem={({ item: metric, index }) => <PressableStyled
        style={styles.metricButton}
        onPress={() => setSelectedMetric(metric)}
      >
        <View style={[
          styles.spot,
          metric.thresholds && {
            backgroundColor: safetyLevelTextColor(metric, liveValues[index]),
          },
        ]} />
        <Text style={styles.metricName}>{metric.title}</Text>
        <Text
          style={styles.unit}
          numberOfLines={1}
          ellipsizeMode='middle'
        >
          {metric.unit}
        </Text>
        <Text style={styles.metricName}>
          {Number.isFinite(liveValues[index]) ? Math.round(liveValues[index] * 10) / 10 : '-'}
        </Text>
        <FontAwesome
          name='angle-right'
          color={Colors.light.hereworksBlue500}
          size={24}
          style={styles.arrowIcon}
        />
      </PressableStyled>}
      keyExtractor={(metric: MetricProps) => `metric_${metric.title}_${metric.unit}`}
    />
    {!!parametersInfoContent && <SelectionModal
      isVisible={descriptionModalVisible}
      hide={() => setDescriptionModalVisible(false)}
      listContent={parametersInfoContent}
      title='About parameters'
      style={styles.descriptionModal}
    />}
  </View>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  infoLabel: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue500,
    marginEnd: 4,
  },
  parameterLongName: {
    fontSize: 16,
    lineHeight: 20,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginTop: 16,
    marginBottom: 12,
  },
  descriptionModal: {
    maxHeight: '100%',
  },
  list: {
    marginTop: 16,
    paddingHorizontal: 24,
    marginHorizontal: -24,
  },
  metricButton: {
    ...Style.row,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 4,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksWhite300,
    backgroundColor: '#FFF',
  },
  metricName: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginStart: 8,
    marginEnd: 4,
  },
  spot: {
    width: 16,
    aspectRatio: 1,
    borderRadius: 100,
    borderWidth: 4,
    borderColor: '#FFFC',
    backgroundColor: Colors.light.hereworksPrimaryGrey600,
  },
  unit: {
    flex: 1,
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  arrowIcon: {
    marginStart: 12,
  },
});

export default OverviewMobile;
